import { IAccountsReceivable, IBenefit, ICESNumber, IDepartment, IFeesAndPremiums, IFlexibleField, IFlexibleFieldSet, IGroupConfigurationData, IGroupNumber, INonStandard, INonStandardTemplate, IPlan, IRef, IStopLoss, ITierLevel } from "model/FlexibleAttributeModel";
import { BaseJsonTransformer, ETransformContextType, ITransformContext } from "./IJsonTransformer";
import { v4 as uuidv4 } from 'uuid';

class IdMapper {
    readonly oldToNewMap = new Map<string, string>();
    readonly newIdMap = new Map<string, string>();

    /**
     * Returns the new id for the given id. If the given id is an old id, then it's new id is returned.
     * If a new id hasn't been generated for that id yet, then it's created and stored for later use. If
     * the given id is a new id, then the new id is returned.
     * @param id either the old or new id
     * @returns the new id
     */
    getId(id: string): string {
        let newId = this.oldToNewMap.get(id);

        // map the id, assuming the id is an old id
        if (newId)
            return newId;

        // see if the id is a new id
        if (this.newIdMap.has(id))
            return id;

        // it's an old id, so we need to generate a new id for it
        newId = uuidv4();
        this.oldToNewMap.set(id, newId);
        this.newIdMap.set(newId, newId);

        return newId;
    }

    clear(): void {
        this.oldToNewMap.clear();
        this.newIdMap.clear();
    }
}

export interface IReplaceIdsScope {
    applyToGroupConfiguration?: boolean;
    applyToBenefits?: boolean;
    applyToPlans?: boolean;
    applyToAccountsReceivables?: boolean;
    applyToFeesAndPremiums?: boolean;
    applyToStopLoss?: boolean;
    applyToGroupStructure?: boolean;
    applyToNonstandardTemplates?: boolean;
}

export class ReplaceIdsTransformer extends BaseJsonTransformer {
    private readonly scope: IReplaceIdsScope;
    private readonly idMapper: IdMapper = new IdMapper();

    constructor(scope?: IReplaceIdsScope) {
        super();

        this.scope = scope || {
            applyToBenefits: true,
            applyToAccountsReceivables: true,
            applyToFeesAndPremiums: true,
            applyToGroupConfiguration: true,
            applyToGroupStructure: true,
            applyToPlans: true,
            applyToStopLoss: true,
            applyToNonstandardTemplates: true,
        };
    }

    async before(json: IGroupConfigurationData): Promise<void> {
        this.idMapper.clear();
    }


    async transformGroupConfiguration(json: IGroupConfigurationData): Promise<void> {
        if (this.scope.applyToGroupConfiguration) {
            this.processFields(json.fields);
        }
    }

    async transformAttributeSet(attributeSet: IFlexibleFieldSet, context: ITransformContext): Promise<boolean | void> {
        if ((this.scope.applyToAccountsReceivables && context.type === ETransformContextType.AccountsReceivable) ||
            (this.scope.applyToBenefits && context.type === ETransformContextType.Benefit) ||
            (this.scope.applyToFeesAndPremiums && context.type === ETransformContextType.FeesAndPremium) ||
            (this.scope.applyToGroupConfiguration && context.type === ETransformContextType.GroupConfiguration) ||
            (this.scope.applyToPlans && context.type === ETransformContextType.Plan) ||
            (this.scope.applyToStopLoss && context.type === ETransformContextType.StopLoss)) {
            this.processAttributeSet(attributeSet);
        }
    }

    async transformNonStandard(nonStandard: INonStandard, context: ITransformContext): Promise<boolean | void> {
        if ((this.scope.applyToAccountsReceivables && context.type === ETransformContextType.AccountsReceivable) ||
            (this.scope.applyToBenefits && context.type === ETransformContextType.Benefit) ||
            (this.scope.applyToFeesAndPremiums && context.type === ETransformContextType.FeesAndPremium) ||
            (this.scope.applyToGroupConfiguration && context.type === ETransformContextType.GroupConfiguration) ||
            (this.scope.applyToPlans && context.type === ETransformContextType.Plan) ||
            (this.scope.applyToStopLoss && context.type === ETransformContextType.StopLoss)) {
            this.processNonStandard(nonStandard);
        }
    }

    async transformBenefit(benefit: IBenefit): Promise<boolean | void> {
        if (this.scope.applyToBenefits) {
            if (benefit.id)
                benefit.id = this.idMapper.getId(benefit.id);

            this.processTiers(benefit.tiers)
            this.processFields(benefit.fields);
        }
    }

    async transformAccountsReceivable(accountsReceivable: IAccountsReceivable): Promise<boolean | void> {
        if (this.scope.applyToAccountsReceivables) {
            if (accountsReceivable.id)
                accountsReceivable.id = this.idMapper.getId(accountsReceivable.id);

            this.processFields(accountsReceivable.fields);
        }
    }

    async transformStopLoss(stopLoss: IStopLoss): Promise<boolean | void> {
        if (this.scope.applyToStopLoss) {
            if (stopLoss.id)
                stopLoss.id = this.idMapper.getId(stopLoss.id);

            this.processFields(stopLoss.fields);
        }
    }

    async transformFeesAndPremium(feesAndPremium: IFeesAndPremiums): Promise<boolean | void> {
        if (this.scope.applyToFeesAndPremiums) {
            if (feesAndPremium.id)
                feesAndPremium.id = this.idMapper.getId(feesAndPremium.id);

            this.processFields(feesAndPremium.fields);
        }

        if (this.scope.applyToStopLoss) {
            this.processRef(feesAndPremium.stopLossRef);
        }
    }

    async transformNonStandardTemplate(nonStandardTemplate: INonStandardTemplate): Promise<boolean | void> {
        // non standard templates don't have ids or refs, so nothing to do here
        if (this.scope.applyToNonstandardTemplates) {
            if (nonStandardTemplate.id)
                nonStandardTemplate.id = this.idMapper.getId(nonStandardTemplate.id);

            nonStandardTemplate.relationships?.forEach(relationship => {
                relationship.sectionRefs?.forEach(sectionRef => {
                    sectionRef.nonStandards?.forEach(nonStandard => {
                        this.processRef(nonStandard);
                    });
                });
            });

            this.processFields(nonStandardTemplate.fields);

        }

    }

    async transformPlan(plan: IPlan): Promise<boolean | void> {
        if (this.scope.applyToPlans) {
            if (plan.id)
                plan.id = this.idMapper.getId(plan.id);

            this.processFields(plan.fields);
        }

        if (this.scope.applyToFeesAndPremiums) {
            this.processRef(plan.feesAndPremiumsRef);
        }

        if (this.scope.applyToBenefits) {
            this.processRefs(plan.benefitRefs);
        }
    }

    async transformCESNumber(cesNumber: ICESNumber): Promise<boolean | void> {
        if (this.scope.applyToGroupStructure) {
            if (cesNumber.id)
                cesNumber.id = this.idMapper.getId(cesNumber.id);
        }
    }

    async transformDepartment(department: IDepartment): Promise<boolean | void> {
        if (this.scope.applyToGroupStructure) {
            if (department.id)
                department.id = this.idMapper.getId(department.id);

            this.processRef(department.cesNumberRef);
        }
    }

    async transformGroupNumber(groupNumber: IGroupNumber): Promise<boolean | void> {
        if (this.scope.applyToGroupStructure) {
            if (groupNumber.id)
                groupNumber.id = this.idMapper.getId(groupNumber.id);

            this.processFields(groupNumber.fields);
            this.processRef(groupNumber.cesNumberRef);
        }

        if (this.scope.applyToAccountsReceivables) {
            this.processRef(groupNumber.accountsReceivableRef);
        }

        if (this.scope.applyToPlans) {
            this.processRef(groupNumber.planRef);
        }

        if (this.scope.applyToStopLoss) {
            this.processRef(groupNumber.stopLossRef);
        }
    }

    async after(json: IGroupConfigurationData): Promise<void> {

    }

    private processFields(fields: IFlexibleField[]): void {
        if (!fields)
            return;

        for (let field of fields) {
            if (!field)
                continue;

            if (field.id)
                field.id = this.idMapper.getId(field.id);

            if (!field.values)
                continue;

            for (let value of field.values) {
                if (!value)
                    continue;

                if (value.id)
                    value.id = this.idMapper.getId(value.id);

                if (!value.tierRef)
                    continue;

                if (value.tierRef.id)
                    value.tierRef.id = this.idMapper.getId(value.tierRef.id);
            }
        }
    }

    private processAttributeSet(set: IFlexibleFieldSet): void {
        if (set.id)
            set.id = this.idMapper.getId(set.id);

        this.processFields(set.fields);
    }

    private processNonStandard(nonStandard: INonStandard): void {
        if (nonStandard.id)
            nonStandard.id = this.idMapper.getId(nonStandard.id);

        this.processFields(nonStandard.fields);
    }

    private processTiers(tiers: ITierLevel[]): void {
        if (!tiers)
            return;

        for (let tier of tiers) {
            if (!tier)
                continue;

            if (tier.id)
                tier.id = this.idMapper.getId(tier.id);
        }
    }

    private processRefs(refs: IRef[]): void {
        if (!refs)
            return;

        for (let ref of refs) {
            this.processRef(ref);
        }
    }

    private processRef(ref: IRef | null | undefined): void {
        if (!ref)
            return;

        if (ref.id)
            ref.id = this.idMapper.getId(ref.id);
    }
}